import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { actionNameBuilder } from "../utilities/storeActionNameBuilder";
import { applyResetMiddleware } from "./middleware/resetMiddleware";
import { DEFAULT_SIDEBAR_WIDTH_IN_PX } from "../constants/uiConstants";

interface UIState {
  showSidebar: boolean;
  dataGridViewMode: "list" | "grid";
  dataDrawerMode: "hidden" | "compact" | "fullscreen";
  sidebarWidth: number;

  // media drawer states
  isMediaDrawerVisible: boolean;
  mediaDrawerManuallyClosedByUser: boolean;

  // Added
  shouldClosePopups: boolean;
  shouldZoomToHome: boolean;

  actions: {
    toggleSidebar: () => void;
    setDataGridViewMode: (mode: UIState["dataGridViewMode"]) => void;
    setCommonUIState: (payload: Partial<Omit<UIState, "actions">>) => void;

    // media drawer actions
    setMediaDrawerVisibility: (isVisible: boolean) => void;
    setMediaDrawerManualCloseByUser: (isClosed: boolean) => void;
    setDataDrawerMode: (mode: UIState["dataDrawerMode"]) => void;
    setSidebarWidth: (value: number) => void;

    // Added
    triggerClosePopups: () => void;
    resetClosePopupsFlag: () => void;
    triggerZoomToHome: () => void;
    resetZoomToHomeFlag: () => void;
    openDataDrawerCompact: () => void;
  };
}
const actionName = actionNameBuilder("UI");

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      immer(
        applyResetMiddleware((set, _, store) => ({
          showSidebar: true,
          dataGridViewMode: "grid",
          // media drawer states
          isMediaDrawerVisible: false,
          mediaDrawerManuallyClosedByUser: false,
          dataDrawerMode: "compact",
          sidebarWidth: DEFAULT_SIDEBAR_WIDTH_IN_PX,

          // Added
          shouldClosePopups: false,
          shouldZoomToHome: false,

          // Separate namespace for store actions
          actions: {
            toggleSidebar: () => set((state) => ({ showSidebar: !state.showSidebar }), undefined, actionName("toggleSidebar")),
            setSidebarWidth: (payload) => set(() => ({ sidebarWidth: payload }), undefined, actionName("setSidebarWidth")),
            setDataGridViewMode: (payload) =>
              set(
                (state) => {
                  state.dataGridViewMode = payload;
                },
                undefined,
                actionName("setDataGridViewMode")
              ),

            // media drawer actions
            setMediaDrawerVisibility: (isVisible) =>
              set(
                (state) => {
                  state.isMediaDrawerVisible = isVisible;
                },
                undefined,
                actionName("setMediaDrawerVisibility")
              ),
            setMediaDrawerManualCloseByUser: (isClosed) =>
              set(
                (state) => {
                  state.mediaDrawerManuallyClosedByUser = isClosed;
                },
                undefined,
                actionName("setMediaDrawerManualCloseByUser")
              ),

            setDataDrawerMode: (mode) =>
              set(
                (state) => {
                  state.dataDrawerMode = mode;
                },
                undefined,
                actionName("setDataDrawerMode")
              ),

            triggerClosePopups: () =>
              set(
                (state) => {
                  state.shouldClosePopups = true;
                },
                undefined,
                actionName("triggerClosePopups")
              ),
            resetClosePopupsFlag: () =>
              set(
                (state) => {
                  state.shouldClosePopups = false;
                },
                undefined,
                actionName("resetClosePopupsFlag")
              ),
            triggerZoomToHome: () =>
              set(
                (state) => {
                  state.shouldZoomToHome = true;
                },
                undefined,
                actionName("triggerZoomToHome")
              ),
            resetZoomToHomeFlag: () =>
              set(
                (state) => {
                  state.shouldZoomToHome = false;
                },
                undefined,
                actionName("resetZoomToHomeFlag")
              ),

            openDataDrawerCompact: () =>
              set(
                (state) => {
                  state.dataDrawerMode = "compact";
                },
                undefined,
                actionName("openDataDrawerCompact")
              ),

            setCommonUIState: (payload) => set(() => ({ ...payload }), undefined, actionName("setCommonUIState"))
          }
        }))
      ),
      {
        name: "ui-store",
        partialize: (ctx) => ({
          sidebarWidth: ctx.sidebarWidth
        })
      } // persist same with on reload
    ),
    { name: "UIStore" }
  )
);

// Grouped selector hook for UI store states.
export const useUISelector = () =>
  useUIStore(
    useShallow((state) => ({
      showSidebar: state.showSidebar,
      dataGridViewMode: state.dataGridViewMode,
      isMediaDrawerVisible: state.isMediaDrawerVisible,
      mediaDrawerManuallyClosedByUser: state.mediaDrawerManuallyClosedByUser,
      dataDrawerMode: state.dataDrawerMode,
      sidebarWidth: state.sidebarWidth,
      // Added
      shouldClosePopups: state.shouldClosePopups,
      shouldZoomToHome: state.shouldZoomToHome
    }))
  );

// Hook for store actions
export const useUIActions = () => useUIStore((state) => state.actions);
